// stockPdfExport.js
// Usage: import { exportStockToPdf } from "./stockPdfExport";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * exportStockToPdf
 * @param {Array}  groupedData  - array of { location, bikes[], total }
 * @param {Object} filters      - current filter state (for title/filename)
 */
export const exportStockToPdf = (groupedData, filters = {}) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  const pageW = doc.internal.pageSize.getWidth();

  // ── HELPERS ─────────────────────────────────────────────────────────────────
  const buildTitle = () => {
    const parts = ["Stock Report"];
    if (filters.location) parts.push(`— ${filters.location}`);
    if (filters.month)    parts.push(`| Month: ${new Date(0, filters.month - 1).toLocaleString("default", { month: "long" })}`);
    if (filters.year)     parts.push(`| Year: ${filters.year}`);
    if (filters.fromDate) parts.push(`| From: ${filters.fromDate}`);
    if (filters.toDate)   parts.push(`| To: ${filters.toDate}`);
    return parts.join(" ");
  };

  const addPageHeader = (title, pageNum) => {
    // Blue header bar
    doc.setFillColor(25, 118, 210);
    doc.rect(0, 0, pageW, 14, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(title, 10, 9);

    // Date & page
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Generated: ${new Date().toLocaleString("en-IN")}`,
      pageW - 10, 9,
      { align: "right" }
    );

    // Reset color
    doc.setTextColor(0, 0, 0);
  };

  // ── COLUMNS ──────────────────────────────────────────────────────────────────
  const columns = [
    { header: "#",            dataKey: "slNo" },
    { header: "Date",         dataKey: "date" },
    { header: "Challan",      dataKey: "challanNo" },
    { header: "Model",        dataKey: "model" },
    { header: "SKU",          dataKey: "skuCode" },
    { header: "Colour",       dataKey: "colour" },
    { header: "Engine No",    dataKey: "engine" },
    { header: "Chasis No",    dataKey: "chasis" },
    { header: "Key No",       dataKey: "keyNumber" },
    { header: "Ex-Show (₹)",  dataKey: "exShowroomPrice" },
    { header: "Purchase (₹)", dataKey: "purchasePrice" },
    { header: "Status",       dataKey: "status" },
    { header: "Source",       dataKey: "source" },
  ];

  const bikeToRow = (bike) => ({
    slNo:           bike.slNo || "-",
    date:           bike.date ? new Date(bike.date).toLocaleDateString("en-IN") : "-",
    challanNo:      bike.challanNo || "-",
    model:          bike.model || "-",
    skuCode:        bike.skuCode || "-",
    colour:         bike.colour || "-",
    engine:         bike.engine || "-",
    chasis:         bike.chasis || "-",
    keyNumber:      bike.keyNumber || "-",
    exShowroomPrice: bike.exShowroomPrice != null ? `₹${bike.exShowroomPrice.toLocaleString("en-IN")}` : "-",
    purchasePrice:  bike.purchasePrice != null ? `₹${bike.purchasePrice.toLocaleString("en-IN")}` : "-",
    status:         bike.status || "-",
    source:         bike.source || "-",
  });

  // ── PAGE 1: SUMMARY TABLE ───────────────────────────────────────────────────
  addPageHeader(buildTitle());

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(25, 118, 210);
  doc.text("Summary by Location", 10, 22);
  doc.setTextColor(0, 0, 0);

  const summaryRows = groupedData.map((loc) => [
    loc.location,
    loc.total,
    loc.bikes.filter((b) => b.source === "NEW").length,
    loc.bikes.filter((b) => b.source === "EXCHANGE").length,
    `₹${loc.bikes.reduce((s, b) => s + (b.purchasePrice || 0), 0).toLocaleString("en-IN")}`,
  ]);

  // Grand total
  const grandTotal = groupedData.reduce((s, l) => s + l.total, 0);
  const grandNew   = groupedData.reduce((s, l) => s + l.bikes.filter((b) => b.source === "NEW").length, 0);
  const grandEx    = groupedData.reduce((s, l) => s + l.bikes.filter((b) => b.source === "EXCHANGE").length, 0);
  const grandVal   = groupedData.reduce((s, l) => s + l.bikes.reduce((ss, b) => ss + (b.purchasePrice || 0), 0), 0);

  summaryRows.push(["GRAND TOTAL", grandTotal, grandNew, grandEx, `₹${grandVal.toLocaleString("en-IN")}`]);

  autoTable(doc, {
    startY: 26,
    head: [["Location", "Total Bikes", "NEW", "EXCHANGE", "Total Purchase Value"]],
    body: summaryRows,
    theme: "grid",
    headStyles: { fillColor: [25, 118, 210], textColor: 255, fontStyle: "bold", fontSize: 9 },
    bodyStyles: { fontSize: 9 },
    alternateRowStyles: { fillColor: [245, 248, 255] },
    didParseCell: (data) => {
      // Highlight grand total row
      if (data.row.index === summaryRows.length - 1) {
        data.cell.styles.fillColor  = [21, 101, 192];
        data.cell.styles.textColor  = 255;
        data.cell.styles.fontStyle  = "bold";
      }
    },
    margin: { left: 10, right: 10 },
  });

  // ── PER-LOCATION TABLES ─────────────────────────────────────────────────────
  groupedData.forEach((loc) => {
    if (!loc.bikes.length) return;

    doc.addPage();
    addPageHeader(buildTitle());

    // Location badge
    doc.setFillColor(232, 244, 253);
    doc.roundedRect(10, 18, pageW - 20, 8, 2, 2, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(25, 118, 210);
    doc.text(`📍 ${loc.location}  —  ${loc.total} bike(s)`, 14, 23.5);
    doc.setTextColor(0, 0, 0);

    autoTable(doc, {
      startY: 28,
      columns,
      body: loc.bikes.map(bikeToRow),
      theme: "grid",
      headStyles: {
        fillColor: [25, 118, 210],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 7.5,
        halign: "center",
      },
      bodyStyles: { fontSize: 7.5 },
      alternateRowStyles: { fillColor: [245, 248, 255] },
      columnStyles: {
        0:  { cellWidth: 8,  halign: "center" },  // #
        1:  { cellWidth: 18 },                    // date
        2:  { cellWidth: 18 },                    // challan
        3:  { cellWidth: 32 },                    // model
        4:  { cellWidth: 14 },                    // sku
        5:  { cellWidth: 16 },                    // colour
        6:  { cellWidth: 28 },                    // engine
        7:  { cellWidth: 28 },                    // chasis
        8:  { cellWidth: 14 },                    // key
        9:  { cellWidth: 20, halign: "right" },   // ex-showroom
        10: { cellWidth: 20, halign: "right" },   // purchase
        11: { cellWidth: 14, halign: "center" },  // status
        12: { cellWidth: 16, halign: "center" },  // source
      },
      didDrawPage: (data) => {
        // Footer with page number
        doc.setFontSize(7);
        doc.setTextColor(150);
        doc.text(
          `Page ${doc.internal.getNumberOfPages()}`,
          pageW / 2, doc.internal.pageSize.getHeight() - 5,
          { align: "center" }
        );
        doc.setTextColor(0, 0, 0);
      },
      margin: { left: 10, right: 10, top: 28 },
    });
  });

  // ── FILENAME & SAVE ─────────────────────────────────────────────────────────
  const parts = ["Stock_Report"];
  if (filters.location) parts.push(filters.location);
  if (filters.month)    parts.push(`Month-${filters.month}`);
  if (filters.year)     parts.push(`Year-${filters.year}`);
  if (filters.fromDate) parts.push(`From-${filters.fromDate}`);
  if (filters.toDate)   parts.push(`To-${filters.toDate}`);
  parts.push(new Date().toISOString().split("T")[0]);

  doc.save(`${parts.join("_")}.pdf`);
};